import React, { useState, useEffect, useMemo } from "react";
import { reportsAPI } from "../../utils/reportsAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { useAuth } from "../../context/AuthContext";
import Pagination from "../../components/Pagination";
import { EmptyState, ErrorState } from "../../components/EmptyStates";
import ReportContent from "../../components/ReportContent";

function ViewReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");


  
  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [user]);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    loadReports(currentPage);
  }, [effectiveOrgId, selectedOrgId, currentPage]);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.list({ page_size: 100 });
      const data = response?.data || response;
      const orgList = Array.isArray(data) ? data : data?.results || [];
      setOrganizations(orgList);
      if (!selectedOrgId && orgList.length > 0) {
        setSelectedOrgId(String(orgList[0].id));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const loadReports = async (page = 1) => {
    const orgIdToUse = user?.role === "ADMIN" ? selectedOrgId : effectiveOrgId;

    if (!orgIdToUse) {
      setError("No organization selected");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await reportsAPI.list(orgIdToUse, {
        page,
        page_size: 10,
      });
      const data = response?.data || response;
      const reportsList = Array.isArray(data) ? data : data?.results || [];
      setReports(reportsList);

      if (response?.pagination) {
        setPagination(response.pagination);
      }

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value);
    setCurrentPage(1);
  };

  if (selectedReport) {
    return (
      <div className="page-content">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSelectedReport(null)}
          style={{ marginBottom: "16px" }}
        >
          ← Back to Reports
        </button>

        <div className="card">
          <h2 className="card-title">
            {selectedReport.title || selectedReport.name}
          </h2>
          <p style={{ color: "var(--text-grey)", marginTop: "4px" }}>
            {new Date(
              selectedReport.created_at || Date.now(),
            ).toLocaleDateString()}
            {(selectedReport.author_name || selectedReport.author_email) &&
              ` • By ${selectedReport.author_name || selectedReport.author_email}`}
          </p>

          {selectedReport.description && (
            <div style={{ marginTop: "20px" }}>
              <h3>Description</h3>
              <p>{selectedReport.description}</p>
            </div>
          )}

          <div style={{ marginTop: "20px" }}>
            <h3>Content</h3>
            <ReportContent
              report={selectedReport}
              organizationId={Number(selectedOrgId) || effectiveOrgId}
            />
          </div>

          <div style={{ marginTop: "20px" }}>
            <h3>Status</h3>
            <span
              className={`status-badge status-${selectedReport.status || "completed"}`}
            >
              {selectedReport.status || "completed"}
            </span>
          </div>

          <div style={{ marginTop: "20px" }}>
            <h3>Report Info</h3>
            <div className="chart-placeholder" style={{ marginTop: "8px" }}>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>Author:</strong>{" "}
                {selectedReport.author_name || selectedReport.author_email || "—"}
              </p>
              <p style={{ margin: 0 }}>
                Report generated on{" "}
                {new Date(
                  selectedReport.created_at || Date.now(),
                ).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {user?.role === "ADMIN" && (
        <div className="card" style={{ marginBottom: "16px" }}>
          <div className="form-group" style={{ maxWidth: "420px" }}>
            <label>Select Organization</label>
            <select value={selectedOrgId} onChange={handleOrgChange}>
              <option value="">Select Organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="card">
        <h2 className="card-title">All Reports</h2>
        {loading ? (
          <p>Loading reports...</p>
        ) : error ? (
          <ErrorState
            error={error}
            onRetry={() => loadReports(currentPage)}
          />
        ) : reports.length > 0 ? (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Report Title</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr
                    key={report.id}
                    className={report.is_seen === false ? "row-unseen" : ""}
                  >
                    <td>{report.title || report.name}</td>
                    <td>{report.author_name || report.author_email || "-"}</td>
                    <td>
                      <span
                        className={`status-badge status-${
                          report.status || "completed"
                        }`}
                      >
                        {report.status || "completed"}
                      </span>
                    </td>
                    <td>
                      {new Date(
                        report.created_at || Date.now(),
                      ).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setSelectedReport(report)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <EmptyState
            title="No reports found"
            message="Once organizations start generating reports, they will appear in this list."
          />
        )}
      </div>
    </div>
  );
}

export default ViewReports;
